import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Copy, Trash2, FileDown, X, AlertCircle, Loader2, Users as UsersIcon, Upload as UploadIcon, Clock, Save, Lock } from 'lucide-react';
import { useGetParametreByKeyQuery } from '../../services/coreApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import {
  useSaisieMatricielleMutation,
  useReporterExamenVersDevoirsTraceMutation,
  useNoteCollectiveMutation,
} from '../../services/notesDevoirsApi';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { useGetNotesDevoirsQuery } from '../../services/notesDevoirsApi';
import {
  useGetNotesExamensQuery,
  useReporterDevoirVersExamenTraceMutation,
} from '../../services/notesExamensApi';
import { formatNote } from '../../utils/formatNote';
import { typeFormationForClasse } from '../../utils/typeFormation';
import AutoSaveIndicator from '../../components/ui/AutoSaveIndicator';
import HistoriqueSaisieModal from './HistoriqueSaisieModal';

interface NoteDevoirMatrice {
  id: number;
  etudiant_id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  devoir1: number | null;
  devoir2: number | null;
  moyenne_devoirs: number | null;
  absent_devoir1: boolean;
  absent_devoir2: boolean;
  verrouille: boolean;
}

interface SaisieMatricielleProps {
  classe: string;
  matiereId: number;
  matiereLabel?: string;
  anneeAcademique: string;
  semestre: 'S1' | 'S2';
  onSuccess?: () => void;
}

const SaisieMatricielle: React.FC<SaisieMatricielleProps> = ({
  classe,
  matiereId,
  matiereLabel,
  anneeAcademique,
  semestre,
  onSuccess,
}) => {
  // Récupérer les étudiants de la classe. Le type_formation dépend du suffixe
  // de la classe : "-CS" → étudiants Cours du Soir, sinon FI + DD.
  const { data: etudiants, isLoading: loadingEtudiants } = useGetEtudiantsQuery({ classe, type_formation: typeFormationForClasse(classe) });

  // Récupérer les notes existantes
  const { data: notesExistantes, isLoading: loadingNotes, refetch: refetchNotes } = useGetNotesDevoirsQuery({
    matiere_id: matiereId,
    annee_academique: anneeAcademique,
    semestre: semestre,
  });

  // Récupérer les notes d'examen (session ordinaire) pour cette matière/classe
  // → permet d'indiquer si on peut reporter et d'alimenter le modal de report
  const { data: notesExamen = [], refetch: refetchNotesExamen } = useGetNotesExamensQuery({
    matiere_id: matiereId,
    annee_academique: anneeAcademique,
    semestre: semestre,
    classe: classe,
    est_rattrapage: false,
  });

  const [reporterExamen, { isLoading: isReporting }] = useReporterExamenVersDevoirsTraceMutation();
  const [reporterDevoirVersExamen, { isLoading: isReportingD2E }] = useReporterDevoirVersExamenTraceMutation();
  const [noteCollective, { isLoading: isApplyingCollective }] = useNoteCollectiveMutation();

  const [saisieMatricielle, { isLoading: isSaving }] = useSaisieMatricielleMutation();

  const [notes, setNotes] = useState<NoteDevoirMatrice[]>([]);
  const [modified, setModified] = useState<Set<number>>(new Set());
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Statut auto-save : idle | saving | saved | error
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs des inputs pour navigation clavier (key = "etudiantId-d1" | "etudiantId-d2")
  const inputRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());
  const setRef = useCallback((key: string) => (el: HTMLInputElement | null) => {
    if (el) inputRefs.current.set(key, el);
    else inputRefs.current.delete(key);
  }, []);

  const isLoading = loadingEtudiants || loadingNotes;

  // Initialiser les données quand les étudiants et notes sont chargés
  useEffect(() => {
    if (etudiants && etudiants.length > 0) {
      const initialNotes: NoteDevoirMatrice[] = etudiants.map((etudiant) => {
        // Chercher si une note existe déjà pour cet étudiant
        const noteExistante = notesExistantes?.find(
          (n: any) => n.etudiant === etudiant.id || n.etudiant_matricule === etudiant.matricule
        );

        return {
          id: noteExistante?.id || 0,
          etudiant_id: etudiant.id,
          matricule: etudiant.matricule,
          nom: etudiant.nom,
          prenoms: etudiant.prenoms,
          devoir1: noteExistante?.note1 ? parseFloat(String(noteExistante.note1)) : null,
          devoir2: noteExistante?.note2 ? parseFloat(String(noteExistante.note2)) : null,
          moyenne_devoirs: noteExistante?.moyenne_devoirs ? parseFloat(String(noteExistante.moyenne_devoirs)) : null,
          absent_devoir1: noteExistante?.absent1 || false,
          absent_devoir2: noteExistante?.absent2 || false,
          verrouille: noteExistante?.verrouille || false,
        };
      });
      setNotes(initialNotes);
      setModified(new Set());
    }
  }, [etudiants, notesExistantes]);

  // Calculer la moyenne
  const calculerMoyenne = (devoir1: number | null, absent1: boolean, devoir2: number | null, absent2: boolean): number | null => {
    const n1 = absent1 ? 0 : devoir1;
    const n2 = absent2 ? 0 : devoir2;

    if (n1 !== null && n2 !== null) {
      return Math.round((n1 + n2) / 2 * 100) / 100; // Arrondi à 2 décimales
    }
    return null;
  };

  // Copier la note D1 vers D2 (mêmes notes pour les 2 devoirs)
  const handleCopyD1ToD2 = (etudiantId: number) => {
    const note = notes.find(n => n.etudiant_id === etudiantId);
    if (!note || note.verrouille) return;
    if (note.devoir1 === null) return;
    if (note.absent_devoir2) {
      // Decoche AB pour permettre la saisie
      handleNoteChange(etudiantId, 'absent_devoir2', false);
    }
    handleNoteChange(etudiantId, 'devoir2', String(note.devoir1));
    // Focus le D2 pour qu'on puisse continuer la navigation
    setTimeout(() => {
      const el = inputRefs.current.get(`${etudiantId}-d2`);
      if (el) { el.focus(); el.select(); }
    }, 0);
  };

  // Navigation clavier : flèches haut/bas/gauche/droite + Enter pour avancer
  // + raccourci C/Espace pour copier D1→D2 et passer au suivant
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIdx: number,
    col: 'd1' | 'd2',
  ) => {
    const max = notes.length - 1;

    // Raccourci C ou Espace : copie D1→D2 + passe au D1 du suivant
    // Inputs type="number" donc 'c'/' ' ne produisent pas de saisie utile
    if (e.key === 'c' || e.key === 'C' || e.key === ' ') {
      const note = notes[rowIdx];
      if (!note || note.verrouille || note.devoir1 === null) return;
      e.preventDefault();
      // Copier D1 → D2 (et décocher AB sur D2 si nécessaire)
      setNotes(prev => prev.map(n => {
        if (n.etudiant_id !== note.etudiant_id) return n;
        const updated = { ...n, devoir2: n.devoir1, absent_devoir2: false };
        updated.moyenne_devoirs = calculerMoyenne(
          updated.devoir1, updated.absent_devoir1,
          updated.devoir2, updated.absent_devoir2
        );
        return updated;
      }));
      setModified(prev => new Set(prev).add(note.etudiant_id));
      // Focus D1 du suivant
      if (rowIdx < max) {
        const nextNote = notes[rowIdx + 1];
        setTimeout(() => {
          const el = inputRefs.current.get(`${nextNote.etudiant_id}-d1`);
          if (el) { el.focus(); el.select(); }
        }, 0);
      }
      return;
    }

    let newRow = rowIdx;
    let newCol: 'd1' | 'd2' = col;

    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      if (rowIdx < max) newRow = rowIdx + 1;
      else return;
    } else if (e.key === 'ArrowUp') {
      if (rowIdx > 0) newRow = rowIdx - 1;
      else return;
    } else if (e.key === 'ArrowLeft') {
      // Seulement si curseur en début de champ (sinon laisser le déplacement texte)
      const target = e.currentTarget;
      if (target.selectionStart !== 0) return;
      if (col === 'd2') newCol = 'd1';
      else if (rowIdx > 0) { newRow = rowIdx - 1; newCol = 'd2'; }
      else return;
    } else if (e.key === 'ArrowRight') {
      const target = e.currentTarget;
      if (target.selectionStart !== (target.value?.length ?? 0)) return;
      if (col === 'd1') newCol = 'd2';
      else if (rowIdx < max) { newRow = rowIdx + 1; newCol = 'd1'; }
      else return;
    } else {
      return;
    }

    e.preventDefault();
    const targetNote = notes[newRow];
    if (!targetNote) return;
    const key = `${targetNote.etudiant_id}-${newCol}`;
    const el = inputRefs.current.get(key);
    if (el) {
      el.focus();
      el.select();
    }
  };

  // ── Détection des verrous de saisie (semestre clôturé) ──────────────────
  // Fix 2026-08-27 : les agents ont besoin de voir CLAIREMENT quand un semestre
  // est verrouillé avant de perdre du temps à saisir. Déclaré ici (tôt) pour
  // que handleNoteChange, performSave et le JSX y aient tous accès.
  const { data: pVerrouGlobal } = useGetParametreByKeyQuery('saisie_notes_verrouillee');
  const { data: pVerrouS1 } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1');
  const { data: pVerrouS2 } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2');
  const { data: pVerrouS1Ord } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1_ord');
  const { data: pVerrouS2Ord } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2_ord');
  const isVerrouActif = (p: any): boolean => {
    if (!p) return false;
    const v = (p.valeur_convertie ?? p.valeur ?? '').toString().toLowerCase().trim();
    return v === 'true' || v === '1' || v === 'oui' || v === 'on';
  };
  // Fix 2026-10-15 : ces verrous (config partagé) ne concernent que l'année
  // ACTIVE. Pour une année antérieure, aucun verrou opérationnel ne s'applique.
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const estAnneeActive = !anneeActive || anneeAcademique === anneeActive.libelle;
  const semestreCloture = useMemo(() => {
    if (!estAnneeActive) return null;
    if (isVerrouActif(pVerrouGlobal)) return { motif: 'global', label: 'Saisie fermée globalement' };
    if (semestre === 'S1') {
      if (isVerrouActif(pVerrouS1)) return { motif: 's1', label: 'Semestre impair (S1) clôturé' };
      if (isVerrouActif(pVerrouS1Ord)) return { motif: 's1_ord', label: 'Session ordinaire S1 clôturée' };
    }
    if (semestre === 'S2') {
      if (isVerrouActif(pVerrouS2)) return { motif: 's2', label: 'Semestre pair (S2) clôturé' };
      if (isVerrouActif(pVerrouS2Ord)) return { motif: 's2_ord', label: 'Session ordinaire S2 clôturée' };
    }
    return null;
  }, [pVerrouGlobal, pVerrouS1, pVerrouS2, pVerrouS1Ord, pVerrouS2Ord, semestre, estAnneeActive]);

  // Mettre à jour une note (refuse silencieusement les valeurs hors plage)
  const handleNoteChange = (etudiantId: number, field: string, value: any) => {
    // Fix 2026-08-27 : si le semestre est verrouillé, refuser toute modif
    // (garde-fou local — les inputs sont déjà `disabled` mais on protège aussi
    // les actions par bouton et le auto-save).
    if (semestreCloture) {
      setMessage({ type: 'error', text: `🔒 ${semestreCloture.label} — saisie refusée` });
      return;
    }
    if (field === 'devoir1' || field === 'devoir2') {
      const numValue = value === '' ? null : parseFloat(value);
      if (numValue !== null) {
        if (Number.isNaN(numValue) || numValue < 0 || numValue > 20) {
          setMessage({ type: 'error', text: 'La note doit être comprise entre 0 et 20.' });
          return;
        }
      }
      value = numValue;
    }

    setNotes(prev => prev.map(note => {
      if (note.etudiant_id === etudiantId) {
        const updatedNote = { ...note, [field]: value };

        // Si on coche absent, mettre la note à 0
        if (field === 'absent_devoir1' && value === true) {
          updatedNote.devoir1 = 0;
        }
        if (field === 'absent_devoir2' && value === true) {
          updatedNote.devoir2 = 0;
        }
        // Fix 2026-09-08 : si on saisit une VRAIE note (>0), décocher
        // automatiquement l'AB — sinon la sauvegarde envoyait absent=true
        // + note=X, et le backend continuait de traiter comme AB. Corrige
        // le cas "formateur avait mis AB, l'agent veut mettre la vraie note".
        if (field === 'devoir1' && value !== null && value !== '' && Number(value) > 0) {
          updatedNote.absent_devoir1 = false;
        }
        if (field === 'devoir2' && value !== null && value !== '' && Number(value) > 0) {
          updatedNote.absent_devoir2 = false;
        }

        // Recalculer la moyenne
        updatedNote.moyenne_devoirs = calculerMoyenne(
          updatedNote.devoir1,
          updatedNote.absent_devoir1,
          updatedNote.devoir2,
          updatedNote.absent_devoir2
        );

        return updatedNote;
      }
      return note;
    }));

    setModified(prev => new Set(prev).add(etudiantId));
  };

  // ─── AUTO-SAVE ────────────────────────────────────────────────────────────
  // Sauvegarde silencieuse (utilisée par l'auto-save et l'enregistrement manuel).
  // Ne renvoie pas de message si tout est OK pour éviter de polluer l'UI.
  const performSave = useCallback(async (silent: boolean): Promise<boolean> => {
    // Fix 2026-08-27 : si le semestre est verrouillé, on n'appelle même pas
    // l'API — évite l'erreur backend et l'attente inutile.
    if (semestreCloture) {
      if (!silent) {
        setMessage({ type: 'error', text: `🔒 ${semestreCloture.label} — enregistrement refusé` });
      }
      setAutoSaveStatus('idle');
      return false;
    }
    const notesToUpdate = notes
      .filter(note => modified.has(note.etudiant_id))
      .map(note => ({
        etudiant_id: note.etudiant_id,
        note1: note.devoir1,
        absent1: note.absent_devoir1,
        note2: note.devoir2,
        absent2: note.absent_devoir2,
      }));

    if (notesToUpdate.length === 0) return true;

    setAutoSaveStatus('saving');
    try {
      const result = await saisieMatricielle({
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre: semestre,
        notes: notesToUpdate,
      }).unwrap();

      if (result.erreurs && result.erreurs > 0) {
        setAutoSaveStatus('error');
        if (!silent) {
          setMessage({
            type: 'error',
            text: `${result.crees + result.modifies} notes enregistrées, ${result.erreurs} erreurs`,
          });
        }
        return false;
      }
      setAutoSaveStatus('saved');
      setLastSaveTime(new Date());
      setModified(new Set());
      if (!silent) {
        setMessage({
          type: 'success',
          text: `${result.crees + result.modifies} notes enregistrées avec succès !`,
        });
        refetchNotes();
        onSuccess?.();
      }
      return true;
    } catch (error: any) {
      setAutoSaveStatus('error');
      if (!silent) {
        setMessage({
          type: 'error',
          text: error.data?.error || error.data?.message || 'Erreur lors de l\'enregistrement des notes',
        });
      }
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, modified, matiereId, anneeAcademique, semestre, semestreCloture]);

  // Auto-save désactivé sur demande utilisateur — risquait d'écraser des notes
  // en cours de saisie. La sauvegarde se fait uniquement via le bouton manuel.
  // L'avertissement avant fermeture (useEffect ci-dessous) reste actif pour
  // éviter de perdre les modifications non enregistrées.

  // Avertissement avant fermeture/rechargement si modifications non sauvegardées
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (modified.size > 0) {
        e.preventDefault();
        e.returnValue = ''; // Affiche le dialog "Quitter le site ?"
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [modified]);

  // Enregistrer les modifications (manuel — bouton)
  const handleSave = async () => {
    setMessage(null);
    if (modified.size === 0) {
      setMessage({ type: 'error', text: 'Aucune modification à enregistrer' });
      return;
    }
    await performSave(false);
  };

  // ─── ACTIONS DE MASSE ─────────────────────────────────────────────────────

  // Copier toutes les D1 → D2 (pour les étudiants non verrouillés ayant une D1)
  const handleCopyAllD1ToD2 = () => {
    const eligibles = notes.filter(n => !n.verrouille && n.devoir1 !== null);
    if (eligibles.length === 0) {
      setMessage({ type: 'error', text: 'Aucun étudiant éligible (D1 vide ou ligne verrouillée).' });
      return;
    }
    if (!window.confirm(
      `Copier toutes les notes D1 vers D2 pour ${eligibles.length} étudiant(s) ?\n\n` +
      `Les notes D2 existantes seront écrasées.\n` +
      `Les lignes verrouillées seront ignorées.`
    )) return;

    setNotes(prev => prev.map(n => {
      if (n.verrouille || n.devoir1 === null) return n;
      const updated = { ...n, devoir2: n.devoir1, absent_devoir2: false };
      updated.moyenne_devoirs = calculerMoyenne(
        updated.devoir1, updated.absent_devoir1,
        updated.devoir2, updated.absent_devoir2
      );
      return updated;
    }));
    setModified(prev => {
      const next = new Set(prev);
      eligibles.forEach(n => next.add(n.etudiant_id));
      return next;
    });
    setMessage({
      type: 'success',
      text: `D1 → D2 copié pour ${eligibles.length} étudiant(s). Auto-save dans 2s.`,
    });
  };

  // Effacer toutes les notes D1 et D2 (pour étudiants non verrouillés)
  const handleClearAll = () => {
    const eligibles = notes.filter(n => !n.verrouille && (n.devoir1 !== null || n.devoir2 !== null));
    if (eligibles.length === 0) {
      setMessage({ type: 'error', text: 'Aucune note à effacer.' });
      return;
    }
    if (!window.confirm(
      `⚠ EFFACER les notes D1 et D2 pour ${eligibles.length} étudiant(s) ?\n\n` +
      `Cette action est irréversible.\n` +
      `Les lignes verrouillées seront conservées.`
    )) return;

    setNotes(prev => prev.map(n => {
      if (n.verrouille) return n;
      return {
        ...n,
        devoir1: null,
        devoir2: null,
        absent_devoir1: false,
        absent_devoir2: false,
        moyenne_devoirs: null,
      };
    }));
    setModified(prev => {
      const next = new Set(prev);
      eligibles.forEach(n => next.add(n.etudiant_id));
      return next;
    });
    setMessage({ type: 'success', text: `Notes effacées pour ${eligibles.length} étudiant(s). Auto-save dans 2s.` });
  };

  // Modaux
  const [showReportModal, setShowReportModal] = useState(false);
  const [showCollectiveModal, setShowCollectiveModal] = useState(false);
  const [showReportD2EModal, setShowReportD2EModal] = useState(false);
  const [showHistorique, setShowHistorique] = useState(false);
  // Historique par NOTE (clic droit sur une cellule) — évite requête globale.
  // Contient { noteDevoirId, etudiantLabel } quand ouvert sur une note précise.
  const [noteHistorique, setNoteHistorique] = useState<{ id: number; label: string } | null>(null);

  // Ouvre l'historique restreint à une note spécifique
  const ouvrirHistoriqueNote = useCallback((noteRow: NoteDevoirMatrice) => {
    if (!noteRow.id || noteRow.id <= 0) return;  // note pas encore persistée en DB
    setNoteHistorique({
      id: noteRow.id,
      label: `${noteRow.nom} ${noteRow.prenoms} (${noteRow.matricule})`,
    });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement de la matrice des notes...</p>
        </div>
      </div>
    );
  }

  if (!etudiants || etudiants.length === 0) {
    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
        <p className="text-yellow-700 dark:text-yellow-300">Aucun étudiant trouvé pour la classe {classe}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header condensé (fix 2026-08-27) — une seule ligne compacte pour libérer
          l'espace saisie ; le vrai bouton Enregistrer reste ici + FAB flottant. */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap text-sm text-gray-700 dark:text-gray-300">
          <span className="font-semibold text-gray-900 dark:text-gray-100">{classe}</span>
          <span className="text-gray-400">·</span>
          <span>{notes.length} étudiants</span>
          <span className="text-gray-400">·</span>
          <span>{semestre}</span>
          {notesExamen.length > 0 ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
              title="Des notes d'examen existent — vous pouvez les reporter vers les devoirs.">
              <FileDown size={10} /> Examen ({notesExamen.length})
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-gray-500 dark:text-gray-400"
              title="Pas de notes d'examen pour cette matière/classe.">
              <AlertCircle size={10} /> Pas d'examen
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Indicateur auto-save (compact) */}
          <AutoSaveIndicator status={autoSaveStatus} lastSaveTime={lastSaveTime} pendingCount={modified.size} />
          {/* Bouton Historique (fix 2026-08-27) */}
          <button
            onClick={() => setShowHistorique(true)}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium"
            title="Voir toutes les modifications sur cette matière/classe/semestre"
          >
            <Clock size={16} /> Historique
          </button>
          {/* Bouton Enregistrer en HAUT (toujours visible, avec le FAB en bas) */}
          <button
            onClick={handleSave}
            disabled={modified.size === 0 || isSaving || !!semestreCloture}
            title={semestreCloture ? `🔒 ${semestreCloture.label} — enregistrement refusé` : ''}
            className={`inline-flex items-center gap-1.5 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow ${
              semestreCloture
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            {semestreCloture ? <Lock size={16} /> : (isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />)}
            {semestreCloture ? 'Verrouillé' : (isSaving ? 'Enregistrement…' : `Enregistrer${modified.size > 0 ? ` (${modified.size})` : ''}`)}
          </button>
        </div>
      </div>

      {/* Bandeau SEMESTRE CLÔTURÉ (fix 2026-08-27) — très visible pour éviter
          que l'agent perde du temps à saisir des notes qui seront refusées. */}
      {semestreCloture && (
        <div className="rounded-lg border-2 border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 flex items-center gap-3">
          <Lock size={22} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-red-800 dark:text-red-200 text-sm uppercase tracking-wide">
              🔒 Saisie verrouillée — {semestreCloture.label}
            </div>
            <div className="text-xs text-red-700 dark:text-red-300 mt-0.5">
              Vos modifications seront <strong>refusées</strong> à l'enregistrement.
              Contactez le super-administrateur pour rouvrir la saisie.
            </div>
          </div>
        </div>
      )}

      {/* Barre d'actions de masse */}
      <div
        className={`bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700 rounded-lg px-4 py-2.5 flex items-center gap-2 flex-wrap ${
          semestreCloture ? 'opacity-50 pointer-events-none select-none' : ''
        }`}
        aria-disabled={!!semestreCloture}
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300 mr-2">
          Actions de masse :
        </span>
        <button
          onClick={handleCopyAllD1ToD2}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm"
          title="Copier toutes les notes D1 dans D2 (utile si 1 seul devoir réel mais 2 notes attendues)"
        >
          <Copy size={13} /> Copier D1 → D2 (tous)
        </button>
        <button
          onClick={() => setShowCollectiveModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-md shadow-sm"
          title="Attribuer une note collective à tous les étudiants (D1, D2 ou les deux)"
        >
          <UsersIcon size={13} /> Note collective
        </button>
        <button
          onClick={() => setShowReportModal(true)}
          disabled={notesExamen.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-md shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          title={notesExamen.length === 0
            ? 'Aucune note d\'examen disponible pour cette matière/classe'
            : `Reporter les ${notesExamen.length} note(s) d'examen vers D1/D2`}
        >
          <FileDown size={13} /> Examen → Devoirs
        </button>
        <button
          onClick={() => setShowReportD2EModal(true)}
          disabled={notes.filter(n => !n.verrouille && (n.devoir1 !== null || n.devoir2 !== null)).length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded-md shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          title="Reporter une note de devoir (D1, D2 ou moyenne) vers la note d'examen"
        >
          <UploadIcon size={13} /> Devoirs → Examen
        </button>
        <span className="ml-auto text-xs text-gray-400">|</span>
        <button
          onClick={handleClearAll}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded-md"
          title="Effacer toutes les notes D1 et D2 (saisi ailleurs garde sa note)"
        >
          <Trash2 size={13} /> Tout effacer
        </button>
      </div>

      {/* Modal Note Collective */}
      {showCollectiveModal && (
        <NoteCollectiveModal
          notes={notes}
          onClose={() => setShowCollectiveModal(false)}
          onConfirm={async (champ, valeur, ecraser) => {
            try {
              await noteCollective({
                matiere_id: matiereId,
                annee_academique: anneeAcademique,
                semestre,
                classes: [classe],
                champ,
                valeur,
                ecraser_existants: ecraser,
              }).unwrap();
              setMessage({
                type: 'success',
                text: `Note collective ${valeur}/20 attribuée (${champ === 'les_deux' ? 'D1+D2' : champ === 'note1' ? 'D1' : 'D2'}).`,
              });
              setShowCollectiveModal(false);
              refetchNotes();
            } catch (err: any) {
              setMessage({ type: 'error', text: err?.data?.error || 'Erreur attribution' });
            }
          }}
          isApplying={isApplyingCollective}
        />
      )}

      {/* Modal Report Devoir → Examen */}
      {showReportD2EModal && (
        <ReportDevoirVersExamenModal
          notesDevoir={notes}
          notesExamen={notesExamen}
          onClose={() => setShowReportD2EModal(false)}
          onConfirm={async (source, mode, etudiantsIds) => {
            try {
              await reporterDevoirVersExamen({
                matiere_id: matiereId,
                annee_academique: anneeAcademique,
                semestre,
                etudiants_ids: etudiantsIds,
                source,
                mode,
                motif: 'Report devoir vers examen depuis interface saisie matricielle',
              }).unwrap();
              setMessage({
                type: 'success',
                text: `${etudiantsIds.length} note(s) reportée(s) du devoir vers l'examen.`,
              });
              setShowReportD2EModal(false);
              refetchNotesExamen();
            } catch (err: any) {
              setMessage({ type: 'error', text: err?.data?.error || 'Erreur lors du report' });
            }
          }}
          isReporting={isReportingD2E}
        />
      )}

      {/* Modal Report Examen */}
      {showReportModal && (
        <ReportExamenModal
          notesDevoir={notes}
          notesExamen={notesExamen}
          onClose={() => setShowReportModal(false)}
          onConfirm={async (mode, etudiantsIds) => {
            try {
              const res = await reporterExamen({
                matiere_id: matiereId,
                annee_academique: anneeAcademique,
                semestre,
                etudiants_ids: etudiantsIds,
                mode,
                motif: 'Report depuis interface saisie matricielle',
              }).unwrap();
              setMessage({
                type: 'success',
                text: `${etudiantsIds.length} note(s) reportée(s) avec succès.`,
              });
              setShowReportModal(false);
              refetchNotes();
              refetchNotesExamen();
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              return res;
            } catch (err: any) {
              setMessage({
                type: 'error',
                text: err?.data?.error || 'Erreur lors du report',
              });
            }
          }}
          isReporting={isReporting}
        />
      )}

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Aide raccourcis clavier */}
      <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-3 px-1">
        <span className="font-semibold text-gray-600 dark:text-gray-300">Raccourcis :</span>
        <span>
          <kbd className="px-1 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 rounded border border-amber-300 font-bold">C</kbd>
          {' '}ou{' '}
          <kbd className="px-1 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 rounded border border-amber-300 font-bold">Espace</kbd>
          {' '}copie D1→D2 + étudiant suivant
        </span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">↑</kbd>/<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">↓</kbd> étudiant précédent/suivant</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">←</kbd>/<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">→</kbd> champ précédent/suivant</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">Entrée</kbd> ligne suivante</span>
        <span className="text-blue-600 dark:text-blue-400">🕒 clic droit sur une note = historique de la note</span>
      </div>

      {/* Tableau (compact pour tenir sans scrollbar horizontale) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
            <colgroup>
              <col style={{ width: '110px' }} />
              <col />
              <col style={{ width: '120px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '70px' }} />
              <col style={{ width: '90px' }} />
            </colgroup>
            <thead className="bg-gray-50 dark:bg-gray-700/50 [&_th]:border-r [&_th]:border-gray-200 dark:[&_th]:border-gray-700 [&_th:last-child]:border-r-0">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Matricule
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Nom & Prénoms
                </th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Devoir 1
                </th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Devoir 2
                </th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Moy
                </th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Statut
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 [&_tr>td]:border-r [&_tr>td]:border-gray-200 dark:[&_tr>td]:border-gray-700 [&_tr>td:last-child]:border-r-0">
              {notes.map((note, rowIdx) => (
                <tr key={note.etudiant_id} className={modified.has(note.etudiant_id) ? 'bg-yellow-50 dark:bg-yellow-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}>
                  <td className="px-2 py-2 whitespace-nowrap text-xs font-mono text-gray-900 dark:text-gray-100 truncate text-left" title={note.matricule}>
                    {note.matricule}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 truncate text-left" title={`${note.nom} ${note.prenoms}`}>
                    <div className="font-semibold text-sm truncate">{note.nom}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{note.prenoms}</div>
                  </td>
                  {/* Devoir 1 — case AB réintroduite (fix 2026-09-08). Un
                      AB coché met la note à 0 ; taper une note >0 décoche
                      automatiquement l'AB. Clic droit → historique. */}
                  <td className="px-1 py-1 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <input
                        ref={setRef(`${note.etudiant_id}-d1`)}
                        type="number"
                        step="0.25"
                        min="0"
                        max="20"
                        value={note.absent_devoir1 ? '' : (note.devoir1 !== null ? note.devoir1 : '')}
                        onChange={(e) => handleNoteChange(note.etudiant_id, 'devoir1', e.target.value)}
                        onBlur={(e) => {
                          if (e.target.value.trim() === '' && !note.verrouille && !note.absent_devoir1) {
                            handleNoteChange(note.etudiant_id, 'devoir1', '0');
                          }
                        }}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 'd1')}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          ouvrirHistoriqueNote(note);
                        }}
                        disabled={note.verrouille || !!semestreCloture || note.absent_devoir1}
                        placeholder={note.absent_devoir1 ? 'AB' : ''}
                        title={semestreCloture
                          ? `🔒 ${semestreCloture.label} — saisie refusée`
                          : (note.absent_devoir1 ? 'Décochez AB pour saisir' : "Clic droit : voir l'historique de cette note")}
                        className={`w-16 px-1 py-1 text-center text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          note.absent_devoir1
                            ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 placeholder-red-500'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600'
                        }`}
                      />
                      <label className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-gray-500 cursor-pointer select-none" title="Absent D1">
                        <input
                          type="checkbox"
                          checked={!!note.absent_devoir1}
                          onChange={(e) => handleNoteChange(note.etudiant_id, 'absent_devoir1', e.target.checked)}
                          disabled={note.verrouille || !!semestreCloture}
                          className="w-3 h-3 accent-red-600"
                        />
                        AB
                      </label>
                      {/* Petit bouton horloge — historique de cette note */}
                      {note.id > 0 && (
                        <button
                          type="button"
                          onClick={() => ouvrirHistoriqueNote(note)}
                          title="Historique de cette note"
                          className="p-0.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                        >
                          <Clock size={11} />
                        </button>
                      )}
                    </div>
                  </td>
                  {/* Devoir 2 : bouton copie D1→D2 + input + icône historique */}
                  <td className="px-1 py-1 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => handleCopyD1ToD2(note.etudiant_id)}
                        disabled={note.devoir1 === null || note.verrouille}
                        title={note.devoir1 === null
                          ? 'Saisir d\'abord le devoir 1'
                          : `Copier ${note.devoir1} du devoir 1 vers devoir 2`}
                        className="p-0.5 rounded text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed"
                      >
                        <ArrowRight size={14} />
                      </button>
                      <input
                        ref={setRef(`${note.etudiant_id}-d2`)}
                        type="number"
                        step="0.25"
                        min="0"
                        max="20"
                        value={note.absent_devoir2 ? '' : (note.devoir2 !== null ? note.devoir2 : '')}
                        onChange={(e) => handleNoteChange(note.etudiant_id, 'devoir2', e.target.value)}
                        onBlur={(e) => {
                          if (e.target.value.trim() === '' && !note.verrouille && !note.absent_devoir2) {
                            handleNoteChange(note.etudiant_id, 'devoir2', '0');
                          }
                        }}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 'd2')}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          ouvrirHistoriqueNote(note);
                        }}
                        disabled={note.verrouille || !!semestreCloture || note.absent_devoir2}
                        placeholder={note.absent_devoir2 ? 'AB' : ''}
                        title={semestreCloture
                          ? `🔒 ${semestreCloture.label} — saisie refusée`
                          : (note.absent_devoir2 ? 'Décochez AB pour saisir' : "Clic droit : voir l'historique de cette note")}
                        className={`w-16 px-1 py-1 text-center text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          note.absent_devoir2
                            ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 placeholder-red-500'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600'
                        }`}
                      />
                      <label className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-gray-500 cursor-pointer select-none" title="Absent D2">
                        <input
                          type="checkbox"
                          checked={!!note.absent_devoir2}
                          onChange={(e) => handleNoteChange(note.etudiant_id, 'absent_devoir2', e.target.checked)}
                          disabled={note.verrouille || !!semestreCloture}
                          className="w-3 h-3 accent-red-600"
                        />
                        AB
                      </label>
                      {note.id > 0 && (
                        <button
                          type="button"
                          onClick={() => ouvrirHistoriqueNote(note)}
                          title="Historique de cette note"
                          className="p-0.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                        >
                          <Clock size={11} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs font-semibold text-gray-900 dark:text-gray-100 text-center">
                    {note.moyenne_devoirs !== null ? formatNote(note.moyenne_devoirs) : '—'}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {note.verrouille ? (
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 rounded-full">
                        Verrouillé
                      </span>
                    ) : modified.has(note.etudiant_id) ? (
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 rounded-full">
                        Modifié
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 rounded-full">
                        Éditable
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAB (Floating Action Button) Enregistrer — rendu via createPortal
          directement dans <body> pour éviter les problèmes de stacking context
          (transform/filter/sticky d'un ancêtre casserait le position:fixed).
          Toujours visible, avec badge count et animate-pulse quand actif.
          Fix 2026-08-27 : plus besoin de remonter en haut pour sauvegarder. */}
      {typeof document !== 'undefined' && createPortal(
        <button
          onClick={handleSave}
          disabled={modified.size === 0 || isSaving || !!semestreCloture}
          title={
            semestreCloture ? `🔒 ${semestreCloture.label} — enregistrement refusé`
              : modified.size === 0 ? 'Aucune modification à enregistrer'
              : isSaving ? 'Enregistrement…'
              : `Enregistrer ${modified.size} modification${modified.size > 1 ? 's' : ''}`
          }
          style={{
            position: 'fixed',
            bottom: '20px',
            // Laisse ~100px à droite pour le FAB de la messagerie instantanée
            // (bottom-5 right-5 + w-14 = ~90px pris par MessagerieFloating).
            right: '100px',
            zIndex: 9999,
          }}
          className={`h-16 w-16 rounded-full shadow-2xl flex items-center justify-center transition-all ${
            semestreCloture
              ? 'bg-red-500 opacity-70 cursor-not-allowed'
              : modified.size === 0
                ? 'bg-gray-400 opacity-60 cursor-not-allowed'
                : isSaving
                  ? 'bg-green-600 opacity-80 cursor-wait'
                  : 'bg-green-600 hover:bg-green-700 hover:scale-110 animate-pulse'
          } text-white`}
        >
          {semestreCloture ? <Lock size={28} />
            : isSaving ? <Loader2 size={28} className="animate-spin" />
            : <Save size={28} />}
          {!semestreCloture && modified.size > 0 && (
            <span
              style={{ position: 'absolute', top: '-4px', right: '-4px' }}
              className="min-w-[26px] h-[26px] px-1.5 flex items-center justify-center text-xs font-bold rounded-full bg-red-500 text-white border-2 border-white dark:border-gray-900 shadow-lg"
            >
              {modified.size}
            </span>
          )}
        </button>,
        document.body
      )}

      {/* Modale historique GLOBAL (bouton 🕒 Historique du header) */}
      <HistoriqueSaisieModal
        open={showHistorique}
        onClose={() => setShowHistorique(false)}
        matiereId={matiereId}
        matiereLabel={matiereLabel}
        classe={classe}
        semestre={semestre}
        anneeAcademique={anneeAcademique}
      />

      {/* Modale historique d'UNE NOTE spécifique (clic droit / icône horloge
          sur une cellule) — requête légère, filtrée sur la note_devoir_id. */}
      <HistoriqueSaisieModal
        open={!!noteHistorique}
        onClose={() => setNoteHistorique(null)}
        noteDevoirId={noteHistorique?.id}
        etudiantLabel={noteHistorique?.label}
        matiereId={matiereId}
        matiereLabel={matiereLabel}
        classe={classe}
        semestre={semestre}
        anneeAcademique={anneeAcademique}
      />
    </div>
  );
};

// ─── Composant : Modal Report Notes Examen ──────────────────────────────────

type ReportMode = 'manquants_seulement' | 'tous';

interface ReportExamenModalProps {
  notesDevoir: NoteDevoirMatrice[];
  notesExamen: any[];
  onClose: () => void;
  onConfirm: (mode: ReportMode, etudiantsIds: number[]) => void;
  isReporting: boolean;
}

const ReportExamenModal: React.FC<ReportExamenModalProps> = ({
  notesDevoir, notesExamen, onClose, onConfirm, isReporting,
}) => {
  const [mode, setMode] = useState<ReportMode>('manquants_seulement');

  // Index des notes devoir par etudiant_id pour comparaison rapide
  const devoirByEtu = useMemo(() => {
    const m = new Map<number, NoteDevoirMatrice>();
    notesDevoir.forEach(n => m.set(n.etudiant_id, n));
    return m;
  }, [notesDevoir]);

  // Lignes affichées : une par note d'examen, enrichie avec l'état devoir
  const lignes = useMemo(() => {
    return notesExamen.map((nx: any) => {
      const dev = devoirByEtu.get(nx.etudiant);
      const aDevoir = dev && (dev.devoir1 !== null || dev.devoir2 !== null);
      const verrouille = dev?.verrouille || false;
      const noteExamen = nx.absent ? null : (nx.note_examen != null ? parseFloat(String(nx.note_examen)) : null);
      return {
        etudiantId: nx.etudiant,
        matricule: nx.etudiant_matricule || '',
        nom: nx.etudiant_nom || '',
        noteExamen,
        absent: nx.absent || false,
        devoir1: dev?.devoir1 ?? null,
        devoir2: dev?.devoir2 ?? null,
        aDevoir,
        verrouille,
      };
    }).sort((a: any, b: any) => (a.nom || '').localeCompare(b.nom || '', 'fr'));
  }, [notesExamen, devoirByEtu]);

  // Filtrer selon le mode pour calculer ce qui sera effectivement reporté
  const lignesAReporter = useMemo(() => {
    return lignes.filter((l: any) => {
      if (l.verrouille) return false;        // jamais d'écrasement sur verrouillé
      if (l.noteExamen === null && !l.absent) return false; // pas de note examen
      if (mode === 'manquants_seulement') return !l.aDevoir;
      return true; // mode 'tous' : on reporte tout (sauf verrouillé)
    });
  }, [lignes, mode]);

  const handleConfirm = () => {
    if (lignesAReporter.length === 0) {
      window.alert('Aucune note à reporter avec le mode choisi.');
      return;
    }
    if (!window.confirm(
      `Confirmer le report de ${lignesAReporter.length} note(s) d'examen vers D1 et D2 ?\n\n` +
      (mode === 'manquants_seulement'
        ? 'Mode : uniquement les étudiants sans note de devoir existante.'
        : 'Mode : ÉCRASER toutes les notes de devoir existantes.\nCette action est irréversible (sauf via Annuler-Batch dans les 24h).')
    )) return;
    onConfirm(mode, lignesAReporter.map((l: any) => l.etudiantId));
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FileDown size={20} className="text-purple-600" />
            Reporter les notes d'examen vers les devoirs
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Choix du mode */}
          <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Comment reporter les notes ?
            </p>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-white dark:hover:bg-gray-700">
              <input
                type="radio"
                name="report-mode"
                value="manquants_seulement"
                checked={mode === 'manquants_seulement'}
                onChange={() => setMode('manquants_seulement')}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                  Uniquement dans les cases vides
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Reporte la note d'examen comme D1 et D2 seulement pour les étudiants qui n'ont
                  encore aucune note de devoir. Préserve les notes existantes.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-white dark:hover:bg-gray-700">
              <input
                type="radio"
                name="report-mode"
                value="tous"
                checked={mode === 'tous'}
                onChange={() => setMode('tous')}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="font-medium text-red-700 dark:text-red-300 text-sm">
                  ⚠ Écraser toutes les notes de devoir existantes
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Remplace D1 et D2 par la note d'examen pour TOUS les étudiants ayant une note d'examen.
                  Les lignes verrouillées sont préservées. Action traçable, annulable via "Annuler batch".
                </p>
              </div>
            </label>
          </div>

          {/* Récap */}
          <div className="bg-purple-50 dark:bg-purple-900/15 border border-purple-200 dark:border-purple-700 rounded-lg p-3 text-sm">
            <strong className="text-purple-900 dark:text-purple-200">{lignesAReporter.length}</strong>
            {' '}note(s) seront reportées sur {lignes.length} note(s) d'examen disponibles
            {lignes.filter((l: any) => l.verrouille).length > 0 && (
              <span className="text-gray-500"> ({lignes.filter((l: any) => l.verrouille).length} verrouillée(s) ignorée(s))</span>
            )}
          </div>

          {/* Aperçu des notes */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Matricule</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Nom</th>
                    <th className="px-2 py-2 text-center font-semibold text-purple-700 dark:text-purple-400">Examen</th>
                    <th className="px-2 py-2 text-center font-semibold text-blue-700 dark:text-blue-400">D1</th>
                    <th className="px-2 py-2 text-center font-semibold text-blue-700 dark:text-blue-400">D2</th>
                    <th className="px-2 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l: any) => {
                    const seraReportee = lignesAReporter.some((r: any) => r.etudiantId === l.etudiantId);
                    return (
                      <tr key={l.etudiantId} className={`border-t border-gray-100 dark:border-gray-700 ${
                        seraReportee ? 'bg-purple-50/50 dark:bg-purple-900/10' : 'opacity-60'
                      }`}>
                        <td className="px-2 py-1.5 font-mono">{l.matricule}</td>
                        <td className="px-2 py-1.5">{l.nom}</td>
                        <td className="px-2 py-1.5 text-center font-bold text-purple-700 dark:text-purple-400">
                          {l.absent ? 'AB' : (l.noteExamen !== null ? formatNote(l.noteExamen) : '—')}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {l.devoir1 !== null ? formatNote(l.devoir1) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {l.devoir2 !== null ? formatNote(l.devoir2) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {l.verrouille ? (
                            <span className="text-red-600 text-[10px]">Verrouillé</span>
                          ) : seraReportee ? (
                            <span className="text-green-600 text-[10px]">→ reportée</span>
                          ) : l.aDevoir && mode === 'manquants_seulement' ? (
                            <span className="text-gray-400 text-[10px]">déjà rempli</span>
                          ) : (
                            <span className="text-gray-400 text-[10px]">ignoré</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isReporting}
            className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={isReporting || lignesAReporter.length === 0}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isReporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            {isReporting ? 'Report en cours…' : `Reporter ${lignesAReporter.length} note(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Composant : Modal Note Collective ──────────────────────────────────────

type ChampCollectif = 'note1' | 'note2' | 'les_deux';

interface NoteCollectiveModalProps {
  notes: NoteDevoirMatrice[];
  onClose: () => void;
  onConfirm: (champ: ChampCollectif, valeur: number, ecraser: boolean) => void;
  isApplying: boolean;
}

const NoteCollectiveModal: React.FC<NoteCollectiveModalProps> = ({
  notes, onClose, onConfirm, isApplying,
}) => {
  const [champ, setChamp] = useState<ChampCollectif>('les_deux');
  const [valeur, setValeur] = useState<string>('');
  const [ecraser, setEcraser] = useState(false);

  // Compteurs : combien d'étudiants seront affectés selon mode
  const stats = useMemo(() => {
    const eligibles = notes.filter(n => !n.verrouille);
    let affectes = 0;
    eligibles.forEach(n => {
      const a1 = champ === 'note1' || champ === 'les_deux';
      const a2 = champ === 'note2' || champ === 'les_deux';
      if (ecraser) { affectes++; return; }
      // Si pas écraser : compter ceux où au moins une cible est vide
      if (a1 && n.devoir1 === null) { affectes++; return; }
      if (a2 && n.devoir2 === null) { affectes++; return; }
    });
    return { total: notes.length, eligibles: eligibles.length, affectes };
  }, [notes, champ, ecraser]);

  const handleConfirm = () => {
    const num = parseFloat(valeur);
    if (Number.isNaN(num) || num < 0 || num > 20) {
      window.alert('Note invalide (entre 0 et 20).');
      return;
    }
    if (stats.affectes === 0) {
      window.alert('Aucun étudiant affecté avec ces paramètres.');
      return;
    }
    if (!window.confirm(
      `Attribuer la note ${num}/20 à ${stats.affectes} étudiant(s) ?\n\n` +
      `Champ : ${champ === 'les_deux' ? 'D1 + D2' : champ === 'note1' ? 'D1 uniquement' : 'D2 uniquement'}\n` +
      `Mode : ${ecraser ? 'ÉCRASER les notes existantes' : 'Remplir uniquement les cases vides'}\n` +
      `Lignes verrouillées préservées.`
    )) return;
    onConfirm(champ, num, ecraser);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md">
        <div className="px-5 py-3 border-b dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <UsersIcon size={20} className="text-amber-600" />
            Note collective
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Attribue la même note à tous les étudiants éligibles de la classe.
          </p>

          {/* Choix du champ */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Appliquer sur
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['note1', 'note2', 'les_deux'] as ChampCollectif[]).map(c => (
                <button
                  key={c}
                  onClick={() => setChamp(c)}
                  className={`px-3 py-2 text-sm font-semibold rounded border-2 transition-colors ${
                    champ === c
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {c === 'note1' ? 'D1 seulement' : c === 'note2' ? 'D2 seulement' : 'D1 + D2'}
                </button>
              ))}
            </div>
          </div>

          {/* Valeur */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Note à attribuer (/20)
            </label>
            <input
              type="number"
              step="0.25"
              min="0"
              max="20"
              value={valeur}
              onChange={e => setValeur(e.target.value)}
              autoFocus
              className="w-full px-4 py-3 text-2xl font-bold text-center border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="Ex: 10"
            />
          </div>

          {/* Écraser ? */}
          <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
            <input
              type="checkbox"
              checked={ecraser}
              onChange={e => setEcraser(e.target.checked)}
              className="mt-1 w-4 h-4 text-amber-600 rounded"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Écraser les notes existantes
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Si décoché : remplit uniquement les cases vides. Sinon : remplace toutes les notes
                non verrouillées.
              </p>
            </div>
          </label>

          {/* Récap */}
          <div className="bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-700 rounded p-3 text-sm">
            <strong>{stats.affectes}</strong> étudiant(s) recevront cette note
            <span className="text-gray-500"> (sur {stats.eligibles} éligibles, {stats.total - stats.eligibles} verrouillé(s))</span>
          </div>
        </div>

        <div className="px-5 py-3 border-t dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isApplying}
            className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={isApplying || !valeur || stats.affectes === 0}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isApplying ? <Loader2 size={14} className="animate-spin" /> : <UsersIcon size={14} />}
            Appliquer
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Composant : Modal Report Devoir → Examen ───────────────────────────────

type SourceDevoir = 'note1' | 'note2' | 'moyenne';

interface ReportDevoirVersExamenModalProps {
  notesDevoir: NoteDevoirMatrice[];
  notesExamen: any[];
  onClose: () => void;
  onConfirm: (source: SourceDevoir, mode: ReportMode, etudiantsIds: number[]) => void;
  isReporting: boolean;
}

const ReportDevoirVersExamenModal: React.FC<ReportDevoirVersExamenModalProps> = ({
  notesDevoir, notesExamen, onClose, onConfirm, isReporting,
}) => {
  const [source, setSource] = useState<SourceDevoir>('moyenne');
  const [mode, setMode] = useState<ReportMode>('manquants_seulement');

  // Index des notes examen par etudiant_id
  const examenByEtu = useMemo(() => {
    const m = new Map<number, any>();
    notesExamen.forEach(n => m.set(n.etudiant, n));
    return m;
  }, [notesExamen]);

  const lignes = useMemo(() => {
    return notesDevoir.map(n => {
      const ex = examenByEtu.get(n.etudiant_id);
      const aExamen = ex && (ex.note_examen != null || ex.absent);
      const sourceVal = source === 'note1' ? n.devoir1 : source === 'note2' ? n.devoir2 : n.moyenne_devoirs;
      return {
        etudiantId: n.etudiant_id,
        matricule: n.matricule,
        nom: n.nom,
        prenoms: n.prenoms,
        devoir1: n.devoir1,
        devoir2: n.devoir2,
        moyenne: n.moyenne_devoirs,
        sourceVal,
        aExamen,
        examenVal: ex ? (ex.absent ? 'AB' : (ex.note_examen != null ? parseFloat(String(ex.note_examen)) : null)) : null,
        verrouille: n.verrouille,
      };
    }).sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr'));
  }, [notesDevoir, examenByEtu, source]);

  const lignesAReporter = useMemo(() => {
    return lignes.filter(l => {
      if (l.sourceVal === null) return false;       // pas de note source
      if (mode === 'manquants_seulement') return !l.aExamen;
      return true;
    });
  }, [lignes, mode]);

  const handleConfirm = () => {
    if (lignesAReporter.length === 0) {
      window.alert('Aucune note à reporter avec ces paramètres.');
      return;
    }
    if (!window.confirm(
      `Reporter ${lignesAReporter.length} note(s) ${source === 'moyenne' ? 'moyenne devoir' : source === 'note1' ? 'D1' : 'D2'} vers la note d'examen ?\n\n` +
      (mode === 'manquants_seulement'
        ? 'Mode : uniquement étudiants sans note examen.'
        : 'Mode : ÉCRASER toutes les notes examen existantes.\nAction traçable & annulable via batch.')
    )) return;
    onConfirm(source, mode, lignesAReporter.map(l => l.etudiantId));
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <UploadIcon size={20} className="text-teal-600" />
            Reporter notes devoir vers examen
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Choix de la source */}
          <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Quelle note de devoir reporter ?
            </p>
            <div className="grid grid-cols-3 gap-2">
              {(['note1', 'note2', 'moyenne'] as SourceDevoir[]).map(s => (
                <button
                  key={s}
                  onClick={() => setSource(s)}
                  className={`px-3 py-2 text-sm font-semibold rounded border-2 transition-colors ${
                    source === s
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {s === 'note1' ? 'D1' : s === 'note2' ? 'D2' : 'Moyenne devoirs'}
                </button>
              ))}
            </div>
          </div>

          {/* Choix du mode */}
          <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Comment reporter ?
            </p>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-white dark:hover:bg-gray-700">
              <input
                type="radio"
                name="report-d2e-mode"
                value="manquants_seulement"
                checked={mode === 'manquants_seulement'}
                onChange={() => setMode('manquants_seulement')}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                  Uniquement dans les cases vides
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Reporte la note seulement pour les étudiants n'ayant pas encore de note d'examen.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-white dark:hover:bg-gray-700">
              <input
                type="radio"
                name="report-d2e-mode"
                value="tous"
                checked={mode === 'tous'}
                onChange={() => setMode('tous')}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="font-medium text-red-700 dark:text-red-300 text-sm">
                  ⚠ Écraser toutes les notes d'examen existantes
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Remplace les notes examen par la note de devoir choisie. Action annulable via batch.
                </p>
              </div>
            </label>
          </div>

          {/* Récap */}
          <div className="bg-teal-50 dark:bg-teal-900/15 border border-teal-200 dark:border-teal-700 rounded-lg p-3 text-sm">
            <strong className="text-teal-900 dark:text-teal-200">{lignesAReporter.length}</strong>
            {' '}note(s) seront reportées sur {lignes.filter(l => l.sourceVal !== null).length} disponible(s)
          </div>

          {/* Aperçu */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold">Matricule</th>
                    <th className="px-2 py-2 text-left font-semibold">Nom</th>
                    <th className="px-2 py-2 text-center font-semibold text-blue-700 dark:text-blue-400">D1</th>
                    <th className="px-2 py-2 text-center font-semibold text-blue-700 dark:text-blue-400">D2</th>
                    <th className="px-2 py-2 text-center font-semibold text-teal-700 dark:text-teal-400">
                      {source === 'note1' ? 'D1' : source === 'note2' ? 'D2' : 'Moy.'}
                    </th>
                    <th className="px-2 py-2 text-center font-semibold text-purple-700 dark:text-purple-400">Examen</th>
                    <th className="px-2 py-2 text-center font-semibold">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map(l => {
                    const seraReportee = lignesAReporter.some(r => r.etudiantId === l.etudiantId);
                    return (
                      <tr key={l.etudiantId} className={`border-t border-gray-100 dark:border-gray-700 ${
                        seraReportee ? 'bg-teal-50/50 dark:bg-teal-900/10' : 'opacity-60'
                      }`}>
                        <td className="px-2 py-1.5 font-mono">{l.matricule}</td>
                        <td className="px-2 py-1.5">{l.nom} {l.prenoms}</td>
                        <td className="px-2 py-1.5 text-center">
                          {l.devoir1 !== null ? formatNote(l.devoir1) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {l.devoir2 !== null ? formatNote(l.devoir2) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center font-bold text-teal-700 dark:text-teal-400">
                          {l.sourceVal !== null ? formatNote(l.sourceVal) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {l.examenVal === 'AB' ? 'AB' : (typeof l.examenVal === 'number' ? formatNote(l.examenVal) : <span className="text-gray-300">—</span>)}
                        </td>
                        <td className="px-2 py-1.5 text-center text-[10px]">
                          {l.sourceVal === null ? (
                            <span className="text-gray-400">pas de source</span>
                          ) : seraReportee ? (
                            <span className="text-green-600">→ reportée</span>
                          ) : l.aExamen && mode === 'manquants_seulement' ? (
                            <span className="text-gray-400">déjà rempli</span>
                          ) : (
                            <span className="text-gray-400">ignoré</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isReporting}
            className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={isReporting || lignesAReporter.length === 0}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isReporting ? <Loader2 size={14} className="animate-spin" /> : <UploadIcon size={14} />}
            {isReporting ? 'Report en cours…' : `Reporter ${lignesAReporter.length} note(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaisieMatricielle;